/**
 * HexFilterBar — the board's filter row: labelled groups of single-choice chips.
 *
 * Chips rather than dropdowns, because on this page the OPTIONS are half the information.
 * "Alerting 3" tells the operator there are three cameras worth looking at before they
 * click anything; the same choice behind a select would hide that number until after they
 * had already decided to go looking for it. Every group is single-choice and always has a
 * selection (an "All" chip is how you clear one), so the row can never end up in a state
 * whose meaning has to be worked out from what ISN'T lit.
 */
export interface FilterChip {
  key: string;
  label: string;
  /** How many cameras this chip would leave on the board. */
  count?: number;
  /** Tailwind background class for a leading dot — severity/state colour. */
  dot?: string;
  title?: string;
}

export interface FilterGroup {
  /** Short heading, e.g. SITE / STATE / ALERT. */
  label: string;
  chips: FilterChip[];
  value: string;
  onChange: (key: string) => void;
}

interface HexFilterBarProps {
  groups: FilterGroup[];
  className?: string;
}

export default function HexFilterBar({ groups, className = '' }: HexFilterBarProps) {
  const shown = groups.filter((g) => g.chips.length > 1);
  if (shown.length === 0) return null;

  return (
    <div
      className={`flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 ${className}`}
    >
      {shown.map((group, i) => (
        <div key={group.label} className="flex min-w-0 flex-wrap items-center gap-1.5">
          {i > 0 && <span className="mr-1 hidden h-5 w-px bg-border sm:block" aria-hidden />}
          <span className="mr-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {group.label}
          </span>
          {group.chips.map((chip) => {
            const active = chip.key === group.value;
            return (
              <button
                key={chip.key}
                type="button"
                aria-pressed={active}
                title={chip.title || chip.label}
                onClick={() => group.onChange(chip.key)}
                className={`flex max-w-[220px] items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'border-brand bg-brand text-white'
                    : 'border-border bg-card text-foreground hover:bg-muted'
                }`}
              >
                {chip.dot && (
                  <span
                    aria-hidden
                    className={`h-2 w-2 flex-none rounded-full ${active ? 'bg-white' : chip.dot}`}
                  />
                )}
                <span className="truncate">{chip.label}</span>
                {chip.count != null && (
                  <span
                    className={`flex-none rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${
                      active ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {chip.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
